import { useState, useMemo, useRef, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import axios from "axios";
import { ArrowRight, ArrowLeft, Check, Sun, Moon, ArrowDown } from "lucide-react";
import SmoothScroll from "@/components/SmoothScroll";
import { chapters, totalChapters } from "@/data/survey";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EASE = [0.76, 0, 0.24, 1];

/* -------------------- Hero -------------------- */
const HeroTitle = () => {
  const lines = ["Transition de vie :", "comment allez-vous", "vraiment ?"];
  return (
    <h1 className="font-serif text-[13vw] md:text-[7.5vw] leading-[0.95] tracking-[-0.03em]">
      {lines.map((l, i) => (
        <span key={i} className="reveal-mask block whitespace-nowrap">
          <motion.span
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.15, ease: EASE, delay: 0.15 + i * 0.12 }}
          >
            {i === 2 ? (<em className="italic">vraiment ?</em>) : l}
          </motion.span>
        </span>
      ))}
    </h1>
  );
};

const Hero = ({ onStart, dark, toggleTheme, hasSaved, onResume, onReset, savedChapter }) => {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 160]);
  const scale = useTransform(scrollY, [0, 800], [1, 1.12]);

  return (
    <section ref={ref} className="relative min-h-screen w-full pt-8 pb-24 md:pt-12 md:pb-40 overflow-hidden">
      {/* Top nav */}
      <div className="w-full max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 flex items-center justify-between">
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.1 }}
          className="flex items-center gap-3"
        >
          <div className="w-9 h-9 rounded-full grid place-items-center border" style={{ borderColor: 'var(--brand-line)' }}>
            <div className="w-2 h-2 rounded-full" style={{ background: 'var(--brand-accent)' }} />
          </div>
          <div className="leading-tight">
            <div className="text-xs uppercase tracking-[0.25em] font-medium">Étude — 2026</div>
            <div className="text-xs uppercase tracking-[0.25em]" style={{ color: 'var(--brand-muted)' }}>Transition de vie</div>
          </div>
        </motion.div>
        <motion.button
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.2 }}
          onClick={toggleTheme}
          data-testid="theme-toggle"
          aria-label="Changer de thème"
          className="w-10 h-10 rounded-full grid place-items-center border transition-colors hover:bg-[var(--brand-line)]"
          style={{ borderColor: 'var(--brand-line)' }}
        >
          {dark ? <Sun size={16} /> : <Moon size={16} />}
        </motion.button>
      </div>

      {/* Hero content */}
      <div className="w-full max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-8">
          <HeroTitle />
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.1 }}
            className="mt-10 text-lg md:text-xl max-w-2xl leading-relaxed"
            style={{ color: 'var(--brand-muted)' }}
          >
            Perte de sens, fatigue de fond, envie de se réinventer. Ce questionnaire écoute
            ce que peu de gens osent nommer — pour bâtir un accompagnement plus juste.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.35 }}
            className="mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-6"
          >
            {hasSaved ? (
              <button
                onClick={onResume}
                data-testid="resume-survey-btn"
                className="group inline-flex items-center gap-4 pl-6 pr-2 py-2 rounded-full text-sm uppercase tracking-[0.2em]"
                style={{ background: 'var(--brand-accent)', color: 'var(--brand-onaccent)' }}
              >
                Reprendre — Chapitre {String(savedChapter).padStart(2, "0")}
                <span className="w-11 h-11 rounded-full grid place-items-center transition-transform group-hover:rotate-[-45deg]" style={{ background: 'var(--brand-onaccent)', color: 'var(--brand-accent)' }}>
                  <ArrowRight size={16} />
                </span>
              </button>
            ) : (
              <button
                onClick={onStart}
                data-testid="start-survey-btn"
                className="group inline-flex items-center gap-4 pl-6 pr-2 py-2 rounded-full text-sm uppercase tracking-[0.2em]"
                style={{ background: 'var(--brand-accent)', color: 'var(--brand-onaccent)' }}
              >
                Commencer le sondage
                <span className="w-11 h-11 rounded-full grid place-items-center transition-transform group-hover:rotate-[-45deg]" style={{ background: 'var(--brand-onaccent)', color: 'var(--brand-accent)' }}>
                  <ArrowRight size={16} />
                </span>
              </button>
            )}
            {hasSaved && (
              <button
                onClick={onReset}
                data-testid="reset-survey-btn"
                className="text-xs uppercase tracking-[0.25em] underline underline-offset-4 hover:opacity-60"
                style={{ color: 'var(--brand-muted)' }}
              >
                Recommencer à zéro
              </button>
            )}
            <div className="flex flex-col text-xs uppercase tracking-[0.25em]" style={{ color: 'var(--brand-muted)' }}>
              <span>10–15 min · 30 questions</span>
              <span>{hasSaved ? "Progression sauvegardée localement" : "Confidentiel"}</span>
            </div>
          </motion.div>
        </div>

        {/* Parallax image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.4, ease: EASE }}
          className="md:col-span-4 relative"
          style={{ y }}
        >
          <div className="relative aspect-[3/4] w-full max-w-[360px] ml-auto overflow-hidden" style={{ borderRadius: 4 }}>
            <motion.img
              src="https://customer-assets-7cd3h4nn.emergentagent.net/job_transition-survey-fr/artifacts/zfb7q8uq_Votre%20texte%20de%20paragraphe.png"
              alt="Route sinueuse vue du ciel — métaphore de la transition"
              className="w-full h-full object-cover"
              style={{ scale }}
            />
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, transparent 60%, rgba(0,0,0,0.35))' }} />
            <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-xs uppercase tracking-[0.2em] text-white/90">
              <span>N° 01 / 09</span>
              <span className="font-serif italic text-base normal-case tracking-normal">le grand virage</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-xs uppercase tracking-[0.25em]"
        style={{ color: 'var(--brand-muted)' }}
      >
        <span>Défiler</span>
        <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
          <ArrowDown size={14} />
        </motion.div>
      </motion.div>
    </section>
  );
};

/* -------------------- Marquee -------------------- */
const EditorialMarquee = () => (
  <section className="py-10 md:py-16 border-y" style={{ borderColor: 'var(--brand-line)' }} aria-hidden="true">
    <Marquee gradient={false} speed={40} pauseOnHover>
      {["Prenez votre temps", "Écoutez-vous", "Aucune mauvaise réponse", "Restez honnête", "Un pas suffit"].map((t, i) => (
        <span key={i} className="font-serif italic text-[10vw] md:text-[7vw] leading-none px-8 flex items-center">
          {t}
          <span className="mx-8 inline-block w-3 h-3 rounded-full" style={{ background: 'var(--brand-accent)' }} />
        </span>
      ))}
    </Marquee>
  </section>
);

/* -------------------- Field components -------------------- */
const Card = ({ selected, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="w-full text-left p-5 md:p-6 border transition-colors text-base md:text-lg"
    style={{
      borderColor: selected ? 'var(--brand-accent)' : 'var(--brand-line)',
      background: selected ? 'color-mix(in srgb, var(--brand-accent) 8%, transparent)' : 'transparent',
      color: 'var(--brand-text)'
    }}
  >
    <div className="flex items-start gap-4">
      <span className="mt-1 w-5 h-5 rounded-full grid place-items-center border flex-shrink-0" style={{ borderColor: selected ? 'var(--brand-accent)' : 'var(--brand-line)', background: selected ? 'var(--brand-accent)' : 'transparent' }}>
        {selected && <Check size={12} color="var(--brand-onaccent)" strokeWidth={3} />}
      </span>
      <span>{children}</span>
    </div>
  </button>
);

const ScaleField = ({ value, onChange, low, high, id }) => (
  <div>
    <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => {
        const active = value === n;
        return (
          <button
            key={n}
            type="button"
            data-testid={`scale-${id}-${n}`}
            onClick={() => onChange(n)}
            className="scale-btn aspect-square border font-serif text-2xl md:text-3xl"
            style={{
              borderColor: active ? 'var(--brand-accent)' : 'var(--brand-line)',
              background: active ? 'var(--brand-accent)' : 'transparent',
              color: active ? 'var(--brand-onaccent)' : 'var(--brand-text)'
            }}
          >
            {n}
          </button>
        );
      })}
    </div>
    <div className="mt-3 flex justify-between text-xs uppercase tracking-[0.2em]" style={{ color: 'var(--brand-muted)' }}>
      <span>1 · {low}</span>
      <span>{high} · 10</span>
    </div>
  </div>
);

const TextInput = ({ value, onChange, placeholder, type = "text", id }) => (
  <input
    type={type}
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    data-testid={`input-${id}`}
    className="w-full bg-transparent border-b py-3 text-lg md:text-2xl font-serif outline-none transition-colors focus:border-[var(--brand-accent)]"
    style={{ borderColor: 'var(--brand-line)', color: 'var(--brand-text)' }}
  />
);

const TextArea = ({ value, onChange, placeholder, id }) => (
  <textarea
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    data-testid={`textarea-${id}`}
    rows={4}
    className="w-full bg-transparent border-b py-3 text-lg md:text-2xl font-serif outline-none transition-colors resize-none focus:border-[var(--brand-accent)]"
    style={{ borderColor: 'var(--brand-line)', color: 'var(--brand-text)' }}
  />
);

const SelectField = ({ value, onChange, options, id }) => (
  <div className="grid gap-3">
    {options.map((opt) => (
      <Card key={opt} selected={value === opt} onClick={() => onChange(opt)} testid={`select-${id}-${opt.slice(0,12)}`}>
        {opt}
      </Card>
    ))}
  </div>
);

const MultiField = ({ value = [], onChange, options, max, id }) => {
  const toggle = (opt) => {
    const arr = value || [];
    if (arr.includes(opt)) onChange(arr.filter((x) => x !== opt));
    else {
      if (max && arr.length >= max) return;
      onChange([...arr, opt]);
    }
  };
  return (
    <div className="grid gap-3">
      {options.map((opt) => (
        <Card key={opt} selected={value.includes(opt)} onClick={() => toggle(opt)} testid={`multi-${id}-${opt.slice(0,12)}`}>
          {opt}
        </Card>
      ))}
    </div>
  );
};

const Question = ({ q, value, onChange, error }) => (
  <div className="mb-14 md:mb-20" data-testid={`question-${q.id}`}>
    <div className="text-xs uppercase tracking-[0.3em] mb-4 flex items-center gap-2" style={{ color: 'var(--brand-muted)' }}>
      <span>Question {String(q.n).padStart(2, "0")}</span>
      {q.required && <span style={{ color: 'var(--brand-accent)' }}>· Requis</span>}
    </div>
    <h3 className="font-serif text-2xl md:text-4xl leading-[1.15] tracking-tight mb-3 max-w-3xl">{q.title}</h3>
    {q.helper && <p className="text-sm mb-6" style={{ color: 'var(--brand-muted)' }}>{q.helper}</p>}
    <div className="mt-6 max-w-3xl">
      {q.type === "select" && <SelectField value={value} onChange={onChange} options={q.options} id={q.id} />}
      {q.type === "multi" && <MultiField value={value} onChange={onChange} options={q.options} id={q.id} />}
      {q.type === "multi-max3" && <MultiField value={value} onChange={onChange} options={q.options} max={3} id={q.id} />}
      {q.type === "radio" && <SelectField value={value} onChange={onChange} options={q.options} id={q.id} />}
      {q.type === "scale" && <ScaleField value={value} onChange={onChange} low={q.low} high={q.high} id={q.id} />}
      {q.type === "text" && <TextInput value={value} onChange={onChange} placeholder={q.placeholder} id={q.id} />}
      {q.type === "email" && <TextInput value={value} onChange={onChange} placeholder={q.placeholder} type="email" id={q.id} />}
      {q.type === "textarea" && <TextArea value={value} onChange={onChange} placeholder={q.placeholder} id={q.id} />}
    </div>
    {error && (
      <motion.div
        initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
        className="mt-4 text-sm inline-flex items-center gap-2 px-3 py-1.5 rounded-full max-w-3xl"
        data-testid={`error-${q.id}`}
        style={{ background: 'color-mix(in srgb, #b0261a 12%, transparent)', color: '#b0261a' }}
      >
        <span>⌁</span> {error}
      </motion.div>
    )}
  </div>
);

/* -------------------- Chapter renderer -------------------- */
const Chapter = ({ chapter, answers, setAnswer, onBack, onNext, isFirst, isLast, submitting, errors }) => (
  <motion.section
    key={chapter.n}
    initial={{ opacity: 0, x: 24 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -24 }}
    transition={{ duration: 0.6, ease: EASE }}
    className="w-full max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-32"
  >
    <div className="grid md:grid-cols-12 gap-8 md:gap-16 mb-16 md:mb-24">
      <div className="md:col-span-4">
        <div className="font-serif text-[18vw] md:text-[10vw] leading-[0.85] tracking-tighter" style={{ color: 'var(--brand-accent)' }}>
          {chapter.n}
        </div>
      </div>
      <div className="md:col-span-8">
        <div className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: 'var(--brand-muted)' }}>
          Chapitre {chapter.n} · sur 09
        </div>
        <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight mb-6">{chapter.title}</h2>
        <p className="text-lg md:text-xl max-w-xl" style={{ color: 'var(--brand-muted)' }}>{chapter.intro}</p>
      </div>
    </div>

    <div className="grid md:grid-cols-12 gap-8">
      <div className="md:col-span-12">
        {chapter.questions.map((q) => (
          <Question key={q.id} q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} error={errors?.[q.id]} />
        ))}
      </div>
    </div>

    <div className="mt-16 pt-10 border-t flex flex-wrap gap-4 items-center justify-between" style={{ borderColor: 'var(--brand-line)' }}>
      <button
        onClick={onBack}
        disabled={isFirst}
        data-testid="chapter-back-btn"
        className="inline-flex items-center gap-3 px-6 py-3 rounded-full border text-sm uppercase tracking-[0.2em] disabled:opacity-40 transition-colors hover:bg-[var(--brand-line)]"
        style={{ borderColor: 'var(--brand-line)' }}
      >
        <ArrowLeft size={14} /> Précédent
      </button>
      <button
        onClick={onNext}
        disabled={submitting}
        data-testid="chapter-next-btn"
        className="group inline-flex items-center gap-4 pl-6 pr-2 py-2 rounded-full text-sm uppercase tracking-[0.2em] disabled:opacity-60"
        style={{ background: 'var(--brand-accent)', color: 'var(--brand-onaccent)' }}
      >
        {isLast ? (submitting ? "Envoi..." : "Envoyer mes réponses") : "Continuer"}
        <span className="w-11 h-11 rounded-full grid place-items-center transition-transform group-hover:rotate-[-45deg]" style={{ background: 'var(--brand-onaccent)', color: 'var(--brand-accent)' }}>
          <ArrowRight size={16} />
        </span>
      </button>
    </div>
  </motion.section>
);

/* -------------------- Thank you -------------------- */
const ThankYou = () => (
  <motion.section
    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE }}
    className="w-full max-w-[1200px] mx-auto px-6 md:px-12 lg:px-20 py-32 md:py-48 text-center"
    data-testid="thank-you-screen"
  >
    <div className="text-xs uppercase tracking-[0.3em] mb-6" style={{ color: 'var(--brand-muted)' }}>Merci profondément</div>
    <h2 className="font-serif text-5xl md:text-8xl leading-[0.95] tracking-tighter">Votre parole a été <em className="italic">reçue</em>.</h2>
    <p className="mt-10 text-lg md:text-xl max-w-xl mx-auto" style={{ color: 'var(--brand-muted)' }}>
      Chaque phrase que vous avez écrite nourrit un accompagnement plus juste, plus humain.
      Prenez soin de vous jusqu’à la prochaine étape.
    </p>
  </motion.section>
);

/* -------------------- Progress bar -------------------- */
const ProgressBar = ({ current, dark, toggleTheme, visible }) => {
  const pct = ((current + 1) / totalChapters) * 100;
  return (
    <div
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b"
      style={{
        borderColor: 'var(--brand-line)',
        background: 'color-mix(in srgb, var(--brand-bg) 85%, transparent)',
        transform: visible ? 'translateY(0)' : 'translateY(-100%)',
        transition: 'transform 400ms cubic-bezier(.76,0,.24,1)'
      }}
    >
      <div className="h-[2px] w-full" style={{ background: 'var(--brand-line)' }}>
        <motion.div
          className="h-full"
          style={{ background: 'var(--brand-accent)' }}
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.8, ease: EASE }}
        />
      </div>
      <div className="w-full max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 py-3 flex items-center justify-between text-xs uppercase tracking-[0.25em]">
        <span>Transition de vie</span>
        <span className="font-serif text-lg tracking-normal normal-case" style={{ color: 'var(--brand-muted)' }}>
          <span style={{ color: 'var(--brand-text)' }}>{String(current + 1).padStart(2, "0")}</span> / {String(totalChapters).padStart(2, "0")}
        </span>
        <button onClick={toggleTheme} aria-label="Changer de thème" data-testid="progress-theme-toggle" className="w-8 h-8 rounded-full grid place-items-center border" style={{ borderColor: 'var(--brand-line)' }}>
          {dark ? <Sun size={14} /> : <Moon size={14} />}
        </button>
      </div>
    </div>
  );
};

/* -------------------- Main page -------------------- */
const STORAGE_KEY = "sondage-transition-v1";

export default function Survey() {
  const [started, setStarted] = useState(false);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [dark, setDark] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.answers && Object.keys(parsed.answers).length > 0) {
          setAnswers(parsed.answers);
          setCurrent(parsed.current || 0);
          setHasSaved(true);
        }
      }
    } catch (e) { /* ignore */ }
    setHydrated(true);
  }, []);

  // Persist to localStorage (skip until hydrated to avoid overwriting)
  useEffect(() => {
    if (!hydrated) return;
    if (done) return;
    if (Object.keys(answers).length === 0 && current === 0) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ answers, current, updated_at: Date.now() }));
    } catch (e) { /* ignore */ }
  }, [answers, current, hydrated, done]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);

  const setAnswer = (id, v) => {
    setAnswers((prev) => ({ ...prev, [id]: v }));
    if (errors[id]) setErrors((e) => { const n = { ...e }; delete n[id]; return n; });
  };

  const startRef = useRef(null);
  const startSurvey = () => {
    setStarted(true);
    setTimeout(() => {
      startRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const resumeSurvey = () => {
    setStarted(true);
    setHasSaved(false);
    setTimeout(() => startRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const resetSurvey = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAnswers({});
    setCurrent(0);
    setErrors({});
    setHasSaved(false);
  };

  const marqueeIdx = 3;
  const isLast = current === totalChapters - 1;

  const validateChapter = () => {
    const chapter = chapters[current];
    const newErrors = {};
    for (const q of chapter.questions) {
      if (!q.required) continue;
      const v = answers[q.id];
      const empty =
        v === undefined || v === null || v === "" ||
        (Array.isArray(v) && v.length === 0);
      if (empty) newErrors[q.id] = "Cette réponse est requise pour continuer.";
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      // scroll to first error
      const firstId = Object.keys(newErrors)[0];
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="question-${firstId}"]`);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return false;
    }
    return true;
  };

  const goNext = async () => {
    if (!validateChapter()) return;
    if (isLast) {
      setSubmitting(true);
      try {
        await axios.post(`${API}/survey`, { answers });
        localStorage.removeItem(STORAGE_KEY);
        setDone(true);
      } catch (e) {
        console.error(e);
        alert("Une erreur est survenue lors de l’envoi. Merci de réessayer.");
      } finally {
        setSubmitting(false);
      }
    } else {
      setCurrent((c) => Math.min(totalChapters - 1, c + 1));
      startRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const goBack = () => {
    setCurrent((c) => Math.max(0, c - 1));
    setErrors({});
    startRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <SmoothScroll>
      <div className="min-h-screen font-sans" style={{ background: 'var(--brand-bg)', color: 'var(--brand-text)' }}>
        <ProgressBar current={current} dark={dark} toggleTheme={() => setDark((d) => !d)} visible={started && !done} />

        {!done && (
          <>
            <Hero
              onStart={startSurvey}
              dark={dark}
              toggleTheme={() => setDark((d) => !d)}
              hasSaved={hasSaved && !started}
              onResume={resumeSurvey}
              onReset={resetSurvey}
              savedChapter={current + 1}
            />

            {started && (
              <div ref={startRef}>
                <AnimatePresence mode="wait">
                  <Chapter
                    key={chapters[current].n}
                    chapter={chapters[current]}
                    answers={answers}
                    setAnswer={setAnswer}
                    onBack={goBack}
                    onNext={goNext}
                    isFirst={current === 0}
                    isLast={isLast}
                    submitting={submitting}
                    errors={errors}
                  />
                </AnimatePresence>

                {current === marqueeIdx && <EditorialMarquee />}
              </div>
            )}
          </>
        )}

        {done && <ThankYou />}

        <footer className="w-full max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 py-16 border-t mt-24" style={{ borderColor: 'var(--brand-line)' }}>
          <div className="flex flex-wrap items-end justify-end gap-8">
            <div className="text-xs uppercase tracking-[0.25em]" style={{ color: 'var(--brand-muted)' }}>
              Réponses confidentielles · Utilisées uniquement pour la recherche
            </div>
          </div>
        </footer>
      </div>
    </SmoothScroll>
  );
}
